<template>
  <div>
    <modal @cancel="closeModal">
      <template #title>
        <p class="modal-card-title">Edit intent</p>
      </template>

      <template #body>
        <div v-if="!loading" class="edit-title-container">
          <h1>
            Name
            <n-tooltip
              v-if="isUnknownIntent()"
              position="right"
              text="The 'Unknown' intent is a system-defined intent and cannot be renamed"
            />
          </h1>
          <div class="field">
            <n-text-input
              class="edit-title"
              v-model="selectedIntentTitle"
              :hideLabel="true"
              label="Intent name"
              :disabled="isUnknownIntent() || isSaveInProgress"
              :errorMessage="errorMessage"
            >
            </n-text-input>
          </div>
        </div>

        <div v-if="!loading" class="edit-utterances-container">
          <h1>Example Utterances</h1>
          <UtteranceList
            v-model="selectedIntentUtterances"
            :v="$v.selectedIntentUtterances"
            term="utterance"
            :is-editable="!isSaveInProgress"
          ></UtteranceList>
        </div>
        <n-loading-spinner v-if="loading" class="loading-spinner" />
      </template>

      <template v-slot:buttons>
        <n-button-group>
          <n-button variant="primary" @click="saveIntent" :loading="isSaveInProgress">Save </n-button>
          <n-button @click="closeModal" variant="primary--outline" :disabled="isSaveInProgress">Cancel</n-button>
        </n-button-group>
      </template>
    </modal>
  </div>
</template>

<script lang="ts" setup>
import Modal from '@/shared/components/Modal.vue';
import { maxLength, required } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import Toast from '@/shared/services/toast';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import { GET_INTENT, UPDATE_INTENT } from '@/store/intents/actionTypes';
import { SaveIntentViewModel } from '@/features/bots/models/intents/saveIntentViewModel';
import { useStore } from 'vuex';
import { computed, onMounted, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

const props = defineProps({
  intentId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'failedToLoad'): void;
  (e: 'closed'): void;
  (e: 'completed', intent: IntentViewModel): void;
}>();

const store = useStore();
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));
const getIntent = (id: string): Promise<IntentViewModel> => store.dispatch(GET_INTENT, id);
const updateIntent = (id: string, intent: SaveIntentViewModel): Promise<IntentViewModel> =>
  store.dispatch(UPDATE_INTENT, { id, intent });

const isSaveInProgress = ref(false);
const selectedIntent = ref<IntentViewModel>({} as IntentViewModel);
const selectedIntentId = ref('');
const selectedIntentTitle = ref('');
const selectedIntentUtterances = ref<UtteranceViewModel[]>([]);
const loading = ref(false);

const validations = {
  selectedIntentTitle: {
    required,
    maxLength: maxLength(50),
    intentNameValid: VuelidateUtil.intentName,
  },
  selectedIntentUtterances: {
    required,
    $each: helpers.forEach({
      text: {
        required,
        isUnique(value: string) {
          return !(
            selectedIntentUtterances.value &&
            selectedIntentUtterances.value.filter((utterance: UtteranceViewModel) => utterance.text === value).length >
              1
          );
        },
      },
    }),
  },
};

const $v = useVuelidate(validations, { selectedIntentTitle, selectedIntentUtterances });

onMounted(async () => {
  loading.value = true;
  await loadIntentWithId();
  setIntentToEdit();
  loading.value = false;
});

async function saveIntent() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    Toast.showError('Fix the errors in red before saving');
    return;
  }
  try {
    if (nlpEnabled()) {
      isSaveInProgress.value = true;
      selectedIntent.value.utterances = selectedIntentUtterances.value.sort(
        (uttOne: UtteranceViewModel, uttTwo: UtteranceViewModel) => uttOne.text.localeCompare(uttTwo.text),
      );
      selectedIntent.value.title = selectedIntentTitle.value.trim();

      const intent = {
        utterances: selectedIntent.value.utterances.map((utterance: UtteranceViewModel) => utterance.text),
        title: selectedIntent.value.title,
      };
      await updateIntent(selectedIntentId.value, intent);

      Toast.showSuccess('Saved intent');
      emit('completed', selectedIntent.value);

      isSaveInProgress.value = false;
      closeModal();
    } else {
      Toast.showError('Unable to save intent - your workspace must have Natural Language support enabled');
      closeModal();
    }
  } catch (ex: any) {
    Toast.showError(ex.message);
    isSaveInProgress.value = false;
  }
}

const errorMessage = computed(() => {
  const intentTitle = $v.value.selectedIntentTitle;
  if (intentTitle.required.$invalid) {
    return 'Name must not be empty';
  } else if (intentTitle.intentNameValid.$invalid) {
    return 'Name must not contain <, >, *, %, &, :, \\, $';
  } else if (intentTitle.maxLength.$invalid) {
    return 'Name must not be longer than 50 characters';
  }

  return '';
});

function isUnknownIntent(): boolean {
  return selectedIntentId.value === 'None';
}

async function loadIntentWithId() {
  try {
    selectedIntent.value = await getIntent(props.intentId);
  } catch (e) {
    Toast.showError('Error loading intent');
    emit('failedToLoad');
  }
}

function setIntentToEdit() {
  if (selectedIntent.value) {
    selectedIntentId.value = selectedIntent.value.id!;
    selectedIntentUtterances.value = selectedIntent.value.utterances.sort(
      (uttOne: UtteranceViewModel, uttTwo: UtteranceViewModel) => uttOne.text.localeCompare(uttTwo.text),
    );
    selectedIntentTitle.value = selectedIntent.value.title;
  }
}

function nlpEnabled() {
  return isWorkspaceFeatureEnabled(NLP_ENABLED).value;
}

function closeModal() {
  selectedIntentId.value = '';
  selectedIntentTitle.value = '';
  selectedIntentUtterances.value = [];
  emit('closed');
}
</script>

<style scoped lang="scss">
.loading-spinner {
  margin: 4rem auto;
}

.utterance-list-item {
  padding: 0.25rem 0.5rem;
  list-style: none;
}

h1 {
  position: relative;
  display: inline-block;
  font-weight: bold;
  left: 10px;
}

.edit-title {
  width: 100%;
  height: 45px;
  background: white;
}

.edit-utterances-container {
  margin-top: 15px;
}

.field {
  padding: 0.25rem 0.5rem;
}
</style>
